import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef, PaginationState, SortingState } from '@tanstack/react-table';
import { FileDown, Plus } from 'lucide-react';
import { getRegistrations } from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import type { Registration } from '../../types';
import { Alert, Badge, Button, DataTable, type BadgeProps } from '@racing/ui';

interface RegistrationsTabProps {
  eventId: number;
}

type Variant = NonNullable<BadgeProps['variant']>;

const paymentStatusVariants: Record<string, Variant> = {
  UNPAID: 'warning',
  PAID: 'success',
  REFUNDED: 'danger',
};

const statusVariants: Record<string, Variant> = {
  PENDING: 'neutral',
  CONFIRMED: 'success',
  CHECKED_IN: 'info',
  CANCELLED: 'danger',
  REFUNDED: 'warning',
};

/** "Last, First", tolerating either half being missing. */
const nameOf = (last?: string | null, first?: string | null) => {
  const l = last || '';
  const f = first || '';
  if (l && f) return `${l}, ${f}`;
  return l || f || '—';
};

const RegistrationsTab = ({ eventId }: RegistrationsTabProps) => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const navigate = useNavigate();

  const loadRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getRegistrations(eventId, {
        page: pagination.pageIndex + 1, // API is 1-indexed.
        pageSize: pagination.pageSize,
        sortField: sorting[0]?.id || 'created_date',
        sortOrder: sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : 'desc',
      });
      setRegistrations(result.data);
      setTotal(result.total);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [eventId, pagination, sorting]);

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  const columns = useMemo<ColumnDef<Registration, any>[]>(
    () => [
      { accessorKey: 'registration_number', header: 'Reg #', meta: { width: 140 } },
      {
        id: 'last_name',
        header: 'Driver',
        accessorFn: (row) => nameOf(row.last_name, row.first_name),
        meta: { width: 170 },
      },
      {
        id: 'class_code',
        header: 'Classes',
        accessorFn: (row) =>
          row.class_entries?.length
            ? row.class_entries.map((e) => e.class_code).filter(Boolean).join(', ')
            : row.class_code || '—',
        meta: { width: 130 },
      },
      {
        id: 'payment_status',
        accessorKey: 'payment_status',
        header: 'Payment',
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return <Badge variant={paymentStatusVariants[value] ?? 'neutral'}>{value}</Badge>;
        },
        meta: { width: 120 },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return <Badge variant={statusVariants[value] ?? 'neutral'}>{value}</Badge>;
        },
        meta: { width: 130 },
      },
      {
        id: 'car_number',
        header: 'Car #',
        accessorFn: (row) =>
          row.class_entries?.length
            ? row.class_entries.map((e) => e.car_number).filter(Boolean).join(', ')
            : row.car_number || '—',
        meta: { width: 110, priority: 'md' },
      },
      {
        id: 'car_count',
        header: 'Cars',
        accessorFn: (row) => row.class_entries?.length || 0,
        meta: { width: 80, numeric: true, priority: 'lg' },
      },
      {
        id: 'app_user_last_name',
        header: 'App User',
        accessorFn: (row) =>
          row.app_user_id ? nameOf(row.app_user_last_name, row.app_user_first_name) : '—',
        meta: { width: 160, priority: 'lg' },
      },
      {
        accessorKey: 'created_date',
        header: 'Date',
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return value ? new Date(value).toLocaleDateString() : '—';
        },
        meta: { width: 120, priority: 'lg' },
      },
    ],
    []
  );

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {error && (
        <Alert variant="danger" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" onClick={() => navigate(`/events/${eventId}/export`)}>
          <FileDown /> Export Compulink
        </Button>
        <Button variant="outline" onClick={() => navigate(`/events/${eventId}/registrations/new`)}>
          <Plus /> Add Registration
        </Button>
      </div>

      <DataTable
        data={registrations}
        columns={columns}
        getRowId={(row) => String(row.registration_id)}
        loading={loading}
        rowCount={total}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        pageSizeOptions={[10, 25, 50]}
        onRowClick={(row) =>
          navigate(`/events/${eventId}/registrations/${row.registration_id}`)
        }
        emptyMessage="No registrations for this event yet"
        className="max-h-[32rem]"
      />
    </div>
  );
};

export default RegistrationsTab;
